import {JsonRpcProvider, FailoverRpcProvider} from '@near-js/providers';
import {Account} from '@near-js/accounts';
import {InMemorySigner} from '@near-js/signers';
import {KeyPairEd25519} from '@near-js/crypto';
import {KeyStore} from '@near-js/keystores';
import {setupWalletSelector} from "@near-wallet-selector/core";
import {setupModal} from "@near-wallet-selector/modal-ui";
import {setupMyNearWallet} from "@near-wallet-selector/my-near-wallet";
import {setupHereWallet} from "@near-wallet-selector/here-wallet";
import {setupSender} from "@near-wallet-selector/sender";
import {useGlobalState, useGlobalMutation} from './utils/container'
import 'regenerator-runtime/runtime';
import "@near-wallet-selector/modal-ui/styles.css";
import {nearService} from './utils/near-service';

import getConfig from './config'
const nearConfig = getConfig(process.env.NODE_ENV || 'development')

// Browser-compatible KeyStore implementation
class BrowserLocalStorageKeyStore extends KeyStore {
  constructor(localStorage = window.localStorage, prefix = 'near-api-js:keystore:') {
    super();
    this.localStorage = localStorage;
    this.prefix = prefix;
  }

  async setKey(networkId, accountId, keyPair) {
    this.localStorage.setItem(this.storageKeyForSecretKey(networkId, accountId), keyPair.toString());
  }

  async getKey(networkId, accountId) {
    const value = this.localStorage.getItem(this.storageKeyForSecretKey(networkId, accountId));
    if (!value) {
      return null;
    }
    return KeyPairEd25519.fromString(value);
  }

  async removeKey(networkId, accountId) {
    this.localStorage.removeItem(this.storageKeyForSecretKey(networkId, accountId));
  }

  async clear() {
    for (const key of this.storageKeys()) {
      if (key.startsWith(this.prefix)) {
        this.localStorage.removeItem(key);
      }
    }
  }

  async getNetworks() {
    const result = new Set();
    for (const key of this.storageKeys()) {
      if (key.startsWith(this.prefix)) {
        const parts = key.substring(this.prefix.length).split(':');
        result.add(parts[1]);
      }
    }
    return Array.from(result);
  }

  async getAccounts(networkId) {
    const result = [];
    for (const key of this.storageKeys()) {
      if (key.startsWith(this.prefix)) {
        const parts = key.substring(this.prefix.length).split(':');
        if (parts[1] === networkId) {
          result.push(parts[0]);
        }
      }
    }
    return result;
  }

  storageKeyForSecretKey(networkId, accountId) {
    return `${this.prefix}${accountId}:${networkId}`;
  }

  *storageKeys() {
    for (let i = 0; i < this.localStorage.length; i++) {
      yield this.localStorage.key(i);
    }
  }
}

// Legacy Contract class for backward compatibility
export class Contract {
  constructor(account, contractId, options) {
    this.account = account;
    this.contractId = contractId;

    const viewMethods = options.viewMethods || [];
    const changeMethods = options.changeMethods || [];

    // Add view methods
    viewMethods.forEach(methodName => {
      this[methodName] = async (args = {}) => {
        return await nearService.callViewFunction(this.contractId, methodName, args);
      };
    });

    // Add change methods
    changeMethods.forEach(methodName => {
      this[methodName] = async (args = {}, gas = '30000000000000', amount = '0') => {
        // For change methods, we need to use wallet selector to sign transactions
        if (!window.selector || !window.accountId) {
          throw new Error('Wallet not connected');
        }

        const wallet = await window.selector.wallet();
        return await wallet.signAndSendTransaction({
          receiverId: this.contractId,
          actions: [{
            type: 'FunctionCall',
            params: {
              methodName,
              args,
              gas,
              deposit: amount
            }
          }]
        });
      };
    });
  }
}

export async function initContract() {
  // Initialize the NEAR service
  await nearService.initialize();
  const provider = nearService.getCurrentProvider();

  // Create a keystore for legacy compatibility
  const keyStore = new BrowserLocalStorageKeyStore();
  const signer = new InMemorySigner(keyStore);

  // Create a connection-like object for backward compatibility
  const connectionObject = {
    provider,
    signer,
    networkId: nearConfig.networkId
  };

  // Initialize wallet selector
  const selector = await setupWalletSelector({
    network: nearConfig.networkId,
    modules: [
      setupMyNearWallet(),
      setupHereWallet(),
      setupSender(),
    ],
  });

  const modal = setupModal(selector, {
    contractId: nearConfig.contractName,
  });

  window.selector = selector;
  window.modal = modal;

  // Subscribe to wallet state changes
  selector.store.observable.subscribe(async (state) => {
    const isSignedIn = selector.isSignedIn();
    if (isSignedIn && state.selectedWalletId) {
      try {
        const wallet = await selector.wallet();
        const accounts = await wallet.getAccounts();
        const accountId = accounts.length > 0 ? accounts[0].accountId : '';
        window.accountId = accountId;
      } catch (error) {
        console.error('Error getting accounts:', error);
      }
    } else if (!isSignedIn) {
      window.accountId = '';
    }
  });

  // Get current wallet and accounts
  const isSignedIn = selector.isSignedIn();
  let accountId = '';

  if (isSignedIn) {
    const wallet = await selector.wallet();
    const accounts = await wallet.getAccounts();
    accountId = accounts.length > 0 ? accounts[0].accountId : '';
  }

  window.accountId = accountId;

  // Create wallet connection for backward compatibility
  window.walletConnection = {
    isSignedIn: () => selector.isSignedIn(),
    getAccountId: () => window.accountId,
    account: () => {
      if (!window.accountId) {
        throw new Error('Wallet not connected');
      }
      return new Account(window.accountId, provider, signer);
    },
    signOut: async () => {
      const wallet = await selector.wallet();
      await wallet.signOut();
      window.accountId = '';
    },
    requestSignIn: async (contractId) => {
      modal.show();
    }
  };

  // Store connection for global access
  window.nearConnection = {
    connection: connectionObject,
    provider
  };

  if (isSignedIn) {
    window.factoryContract = new Contract(window.walletConnection.account(), nearConfig.contractName, {
      viewMethods: ['get_dao_list'],
      changeMethods: ['create'],
    })
  }
}

export async function logout() {
  if (window.selector) {
    const wallet = await window.selector.wallet();
    await wallet.signOut();
    window.accountId = '';
  }
  window.location.replace(window.location.origin + window.location.pathname)
}

export function login() {
  if (window.modal) {
    window.modal.show();
  }
}
